import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import { ROUTES } from '../../config/routes';
import crmEmpresaService from '../../services/crmEmpresaService';
import type { CrmEmpresaListItem } from '../../types/crm';
import { getApiErrorMessage } from '../../utils/apiError';

const CrmEmpresas: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<CrmEmpresaListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (pageNum = page, pageSize = rowsPerPage, search = searchTerm) => {
      try {
        setLoading(true);
        const data = await crmEmpresaService.getAll({
          pageNumber: pageNum + 1,
          pageSize,
          searchTerm: search || undefined,
          activo: true,
        });
        setItems(data.items ?? []);
        setTotalCount(data.totalCount ?? 0);
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar empresas'), { variant: 'error' });
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchTerm, enqueueSnackbar],
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(0);
      fetchData(0, rowsPerPage, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, rowsPerPage]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Empresas CRM
              </Typography>
              <Can permiso="crm.empresas.nuevo">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(ROUTES.crm.empresas.nuevo)}
                >
                  Nueva
                </Button>
              </Can>
            </Stack>

            <TextField
              size="small"
              placeholder="Buscar por nombre, CUIT, email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>CUIT</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Teléfono</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell align="right">Contactos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((e) => (
                    <TableRow
                      key={e.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(ROUTES.crm.empresas.editar(e.id))}
                    >
                      <TableCell>{e.nombre}</TableCell>
                      <TableCell>{e.cuit || '-'}</TableCell>
                      <TableCell>{e.email || '-'}</TableCell>
                      <TableCell>{e.telefono || '-'}</TableCell>
                      <TableCell>
                        {e.nroCliente != null
                          ? `${e.nroCliente}${e.nombreCliente ? ` — ${e.nombreCliente}` : ''}`
                          : '-'}
                      </TableCell>
                      <TableCell align="right">{e.cantidadContactos}</TableCell>
                    </TableRow>
                  ))}
                  {!loading && items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No se encontraron empresas.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                fetchData(newPage, rowsPerPage, searchTerm);
              }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                const size = parseInt(e.target.value, 10);
                setRowsPerPage(size);
                setPage(0);
                fetchData(0, size, searchTerm);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas:"
            />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CrmEmpresas;
